import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { HTTP_ERROR } from "functions/http";
import {
  NotificationsAll,
  NotificationsMarkAllRead,
  NotificationsMarkRead,
} from "functions/http-requests/notifications";
import React, { useCallback, useEffect, useRef, useState } from "react";
import NotificationCard from "./notificationCard";

interface Props {
  id?: string;
  anchorEl?: HTMLButtonElement | null;
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  onChanged?: () => void;
}

interface NotificationItem {
  _id: string;
  type: string;
  title: string;
  body: string;
  level?: string;
  alertLevel?: string;
  isRead: boolean;
  createdAt: string;
  data?: {
    link?: string;
    level?: string;
    alertLevel?: string;
    [key: string]: unknown;
  };
}

const PAGE_SIZE = 25;

const Notifications: React.FC<Props> = ({
  id,
  anchorEl,
  open,
  onClose,
  onChanged,
}) => {
  const { palette } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const hasMoreRef = useRef<boolean>(true);

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState<boolean>(false);
  const [markingReadId, setMarkingReadId] = useState<string>("");
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const hasUnreadNotifications = notifications.some((item) => !item.isRead);

  const getNotifications = useCallback(
    async (targetPage: number, shouldReset = false) => {
      if (isLoadingRef.current) return;
      if (!shouldReset && !hasMoreRef.current) return;

      isLoadingRef.current = true;
      setIsLoading(true);
      setErrorMessage("");

      if (shouldReset) {
        setNotifications([]);
      }

      NotificationsAll({
        DATA: {
          pageNumber: targetPage,
          pageSize: PAGE_SIZE,
          unreadOnly: unreadOnly ? true : undefined,
        },
      })
        .then((response) => {
          let incoming: NotificationItem[] = response?.data?.data || [];
          if (unreadOnly) {
            incoming = incoming.filter((item) => !item.isRead);
          }
          const total: number = response?.data?.meta?.pagination?.total || 0;

          let loadedCount = incoming.length;
          setNotifications((prev) => {
            const next = shouldReset ? incoming : [...prev, ...incoming];
            loadedCount = next.length;
            return next;
          });

          const nextHasMore = loadedCount < total && incoming.length > 0;
          hasMoreRef.current = nextHasMore;
          setHasMore(nextHasMore);
          setPageNumber(targetPage);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) setErrorMessage(HTTP_ERROR(error));
        })
        .finally(() => {
          isLoadingRef.current = false;
          setIsLoading(false);
        });
    },
    [unreadOnly],
  );

  useEffect(() => {
    if (!open) {
      setUnreadOnly(false);
    }

    hasMoreRef.current = true;
    getNotifications(1, true);
  }, [open, unreadOnly, getNotifications]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingRef.current || !hasMoreRef.current) return;

    const nearBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 20;
    if (nearBottom) {
      getNotifications(pageNumber + 1);
    }
  }, [getNotifications, pageNumber]);

  const handleMarkAllRead = useCallback(() => {
    if (isMarkingAllRead || !hasUnreadNotifications) return;
    setErrorMessage("");
    setIsMarkingAllRead(true);

    NotificationsMarkAllRead()
      .then(() => {
        setNotifications([]);
        setHasMore(false);
        hasMoreRef.current = false;
        onChanged?.();
      })
      .catch((error) => setErrorMessage(HTTP_ERROR(error)))
      .finally(() => setIsMarkingAllRead(false));
  }, [hasUnreadNotifications, isMarkingAllRead, onChanged]);

  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      if (!notificationId || markingReadId === notificationId) return;
      setErrorMessage("");
      setMarkingReadId(notificationId);

      NotificationsMarkRead({ ID: notificationId })
        .then(() => {
          setNotifications((prev) => {
            if (unreadOnly) {
              return prev.filter((item) => item._id !== notificationId);
            }
            return prev.map((item) =>
              item._id === notificationId ? { ...item, isRead: true } : item,
            );
          });
          onChanged?.();
        })
        .catch((error) => setErrorMessage(HTTP_ERROR(error)))
        .finally(() => setMarkingReadId(""));
    },
    [markingReadId, onChanged, unreadOnly],
  );
  return (
    <Popover
      id={id}
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: 380,
          maxHeight: "min(520px, calc(100vh - 24px))",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        px={2}
        pt={2}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {notifications?.map((item) => (
            <NotificationCard
              key={item._id}
              id={item._id}
              type={item.type}
              title={item.title}
              body={item.body}
              createdAt={item.createdAt}
              isRead={item.isRead}
              level={item.level || item.data?.level}
              alertLevel={item.alertLevel || item.data?.alertLevel}
              link={item?.data?.link}
              onMarkAsRead={handleMarkAsRead}
              isMarkingRead={markingReadId === item._id}
            />
          ))}

          {isLoading ? (
            <Box
              pb={2}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress
                color="primary"
                sx={{
                  width: "20px !important",
                  height: "20px !important",
                }}
              />
            </Box>
          ) : null}

          {!isLoading && !notifications.length ? (
            <Typography
              px={1}
              pb={2}
              fontSize={12}
              color={palette.text.secondary}
              sx={{
                textAlign: "center",
              }}
            >
              {unreadOnly
                ? "No unread notifications found."
                : "No notifications found."}
            </Typography>
          ) : null}

          {!isLoading && !hasMore && notifications.length > 0 ? (
            <Typography
              px={1}
              py={1}
              fontSize={12}
              color={palette.text.secondary}
              sx={{
                textAlign: "center",
              }}
            >
              You’re all caught up.
            </Typography>
          ) : null}

          {errorMessage ? (
            <Typography px={1} py={1} fontSize={12} color="error.main">
              {errorMessage}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box
        px={2}
        py={1.5}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          position: "sticky",
          bottom: 0,
          backgroundColor: palette.common.white,
          borderTop: `1px solid ${palette.divider}`,
        }}
      >
        <FormControlLabel
          sx={{
            m: 0,
            ".MuiFormControlLabel-label": {
              fontSize: 12,
              color: palette.text.secondary,
            },
          }}
          control={
            <Switch
              size="small"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
          }
          label="Unread only"
        />

        {hasUnreadNotifications ? (
          <Button
            variant="text"
            size="small"
            sx={{
              color: palette.text.secondary,
              fontSize: 12,
              minHeight: 28,
              px: 1,
              py: 0.25,
              textTransform: "capitalize",
            }}
            onClick={handleMarkAllRead}
            disabled={isMarkingAllRead}
          >
            Mark all as read
          </Button>
        ) : null}
      </Box>
    </Popover>
  );
};

export default Notifications;
